// Leave / return markers for the shared Guru user-action recorder
// (kanban 0419d550, process-graph lens "step view").
//
// The recorder's pause summary told the lens that 93% of the portal's long
// pauses are hidden-tab time, but no row said WHICH order or email was open
// when the person left, so the lens could not label the time (Sage, Outlook,
// a phone call). The library's own page_leave fires only on pagehide: a
// hidden tab is not a left page.
//
// This module records two action rows:
//   portal_leave   when the tab goes hidden (visibilitychange) or the window
//                  loses focus (blur) and does not get it back within a beat
//   portal_return  when the tab is visible again / the window regains focus
// each with targets = { surface: <route template>, ...<open objects> }
// (order_id on an open order, email_id on an open email, thread_ref /
// thread_id on the copilots) and values.via = hidden|blur|visible|focus.
// The leave row is flushed at once, with a keepalive fetch, so it lands even
// if the tab is never shown again. Not with navigator.sendBeacon: until the
// platform fix of 2026-10-22 (gurucloudai PR #5490) a beacon was a
// credentialed cross-origin request whose preflight the recorder ingest
// answered without Access-Control-Allow-Credentials, so the browser dropped
// it after sendBeacon() had already returned true and the library never fell
// back to fetch (kanban fb868e60: 0 portal_leave rows against 129
// portal_return rows in the first day on production). The fetch mode,
// fetch(..., {keepalive: true, credentials: 'omit'}), never depended on that
// header, survives a tab close, and returns a reply an E2E can read, so it
// stays the transport for this row.
//
// What is "open" comes from a registry fed by useRecorderObject() in the
// components that hold an object (OrderDetailModal, EmailViewModal, the
// quality thread workspace, the tech-support copilot). Several can be open
// at once (an email with its order in the split view): all ids are merged,
// first registration wins on a key collision.
//
// Object id keys follow the sibling card f0b295e8: order_id, email_id.

import { getGuruRecorder } from '../utils/guruRecorderLoader';
import { resolvePortalScreen } from './portalScreens';

export const PORTAL_LEAVE_ACTION = 'portal_leave';
export const PORTAL_RETURN_ACTION = 'portal_return';

/** A window blur that is followed by focus within this many ms is not a leave (iframe focus, devtools). */
export const BLUR_SETTLE_MS = 250;

/**
 * How the leave row is pushed out. 'fetch' is the library's keepalive fetch,
 * which does not depend on the ingest's CORS treatment of credentialed
 * beacons (the cause of the 2026-10 loss, see the header); 'beacon' works
 * again since the platform fix but gives an E2E no reply to assert on.
 */
export const LEAVE_FLUSH_MODE: 'fetch' | 'beacon' = 'fetch';

export type RecorderObjectTargets = Record<string, string | number | null | undefined>;

// ─── open-object registry ───────────────────────────────────────────────────

let _nextHandle = 1;
const _open = new Map<number, RecorderObjectTargets>();

/**
 * Declare an object as open on the current surface. Returns the function
 * that closes it. Components use the useRecorderObject hook instead.
 */
export function registerRecorderObject(targets: RecorderObjectTargets): () => void {
  const handle = _nextHandle++;
  _open.set(handle, targets);
  return () => {
    _open.delete(handle);
  };
}

/** The ids open right now (insertion order, first wins on a key collision). */
export function openRecorderObjects(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const targets of _open.values()) {
    for (const [key, raw] of Object.entries(targets)) {
      if (raw === null || raw === undefined || raw === '') continue;
      if (out[key] === undefined) out[key] = String(raw);
    }
  }
  return out;
}

// ─── presence ───────────────────────────────────────────────────────────────

type LeaveVia = 'hidden' | 'blur';
type ReturnVia = 'visible' | 'focus';

let _away = false;
let _listening = false;
let _blurTimer: ReturnType<typeof setTimeout> | null = null;

function markerTargets(): Record<string, string> {
  const surface = resolvePortalScreen(window.location.pathname);
  return { surface, ...openRecorderObjects() };
}

function recordLeave(via: LeaveVia): void {
  if (_away) return;
  _away = true;
  try {
    const api = getGuruRecorder();
    if (!api) return;
    api.record(PORTAL_LEAVE_ACTION, { targets: markerTargets(), values: { via }, outcome: 'none' });
    // Push this row out ourselves: the library's own hidden-flush runs in
    // registration order (before or after this listener), so it may already
    // have emptied the queue. The fetch mode is a keepalive fetch, so it
    // outlives a tab that closes next (see the header for why not a beacon).
    api.flush(LEAVE_FLUSH_MODE);
  } catch {
    // recording must never break the page
  }
}

function recordReturn(via: ReturnVia): void {
  if (!_away) return;
  _away = false;
  try {
    getGuruRecorder()?.record(PORTAL_RETURN_ACTION, {
      targets: markerTargets(),
      values: { via },
      outcome: 'none',
    });
  } catch {
    // never break the page
  }
}

function clearBlurTimer(): void {
  if (_blurTimer !== null) {
    clearTimeout(_blurTimer);
    _blurTimer = null;
  }
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'hidden') {
    clearBlurTimer();
    recordLeave('hidden');
  } else {
    recordReturn('visible');
  }
}

function onBlur(): void {
  // A window switch that keeps the tab visible (second monitor, Sage next to
  // the browser) never fires visibilitychange; blur is the only signal. It
  // also fires when focus moves into an iframe on our own page, in which
  // case document.hasFocus() is still true a moment later.
  clearBlurTimer();
  _blurTimer = setTimeout(() => {
    _blurTimer = null;
    if (document.visibilityState === 'hidden') return; // visibilitychange owns it
    if (typeof document.hasFocus === 'function' && document.hasFocus()) return;
    recordLeave('blur');
  }, BLUR_SETTLE_MS);
}

function onFocus(): void {
  clearBlurTimer();
  if (document.visibilityState === 'hidden') return;
  recordReturn('focus');
}

/** Install the leave/return listeners once (mounted by useGuruRecorder). */
export function startRecorderPresence(): void {
  if (_listening || typeof document === 'undefined') return;
  _listening = true;
  _away = false;
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('blur', onBlur);
  window.addEventListener('focus', onFocus);
}

export function stopRecorderPresence(): void {
  if (!_listening) return;
  _listening = false;
  clearBlurTimer();
  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('blur', onBlur);
  window.removeEventListener('focus', onFocus);
  _away = false;
}

/** Test-only. */
export function _resetRecorderPresenceForTests(): void {
  stopRecorderPresence();
  _open.clear();
  _nextHandle = 1;
}
